import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { environment } from '../../environments/environment';

export interface AgentTransactionFilters {
  agentId?: string;
  fromDate?: string;
  toDate?: string;
  type?: string;
  pageIndex?: number;
  pageSize?: number;
}

export interface AgentTransactionItemDto {
  referenceId: string;
  type: string;
  amount: number;
  cgst: number;
  sgst: number;
  totalAmount: number;
  isInvoiced: boolean;
  createdAt: string;
  agentId?: string;
  agentName?: string;
}

export interface AgentTransactionsSummaryDto {
  transactions: AgentTransactionItemDto[];
  totalCount: number;
  pageIndex: number;
  pageSize: number;
  totalAmountSum: number;
}

/** Raw API shape (mixed camelCase / PascalCase). */
interface AgentTransactionsSummaryApiResponse {
  transactions?: AgentTransactionItemApiRow[];
  items?: AgentTransactionItemApiRow[];
  totalCount?: number;
  TotalCount?: number;
  pageIndex?: number;
  PageIndex?: number;
  pageSize?: number;
  PageSize?: number;
  totalAmountSum?: number;
  TotalAmountSum?: number;
}

interface AgentTransactionItemApiRow {
  agentId?: string;
  agentName?: string;
  AgentName?: string;
  type?: string;
  amount?: number;
  cgst?: number;
  sgst?: number;
  totalAmount?: number;
  isInvoiced?: boolean;
  referenceId?: string;
  sourceType?: string;
  createdAt?: string;
  CreatedAt?: string;
  id?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AgentTransactionService {
  private apiUrl = `${environment.apiConfig.uri}/api/AgentInvoice`;

  constructor(private http: HttpClient) {}

  getTransactions(filters: AgentTransactionFilters): Observable<AgentTransactionsSummaryDto> {
    let params = new HttpParams();

    const entries: [string, string | number | undefined][] = [
      ['agentId', filters.agentId],
      ['fromDate', filters.fromDate],
      ['toDate', filters.toDate],
      ['type', filters.type],
      ['pageIndex', filters.pageIndex ?? 0],
      ['pageSize', filters.pageSize ?? 10],
    ];

    for (const [key, value] of entries) {
      if (value !== null && value !== undefined && value !== '') {
        params = params.set(key, String(value));
      }
    }

    return this.http
      .get<AgentTransactionsSummaryApiResponse>(`${this.apiUrl}/transactions`, { params })
      .pipe(map((response) => this.normalizeSummary(response)));
  }

  private normalizeSummary(
    response: AgentTransactionsSummaryApiResponse
  ): AgentTransactionsSummaryDto {
    const rows = response.transactions ?? response.items ?? [];

    return {
      transactions: rows.map((row) => ({
        agentId: row.agentId,
        agentName: row.agentName ?? row.AgentName,
        type: row.type ?? '',
        amount: row.amount ?? 0,
        cgst: row.cgst ?? 0,
        sgst: row.sgst ?? 0,
        totalAmount: row.totalAmount ?? 0,
        isInvoiced: row.isInvoiced ?? false,
        referenceId: row.referenceId ?? row.id ?? '',
        createdAt: row.createdAt ?? row.CreatedAt ?? '',
      })),
      totalCount: response.totalCount ?? response.TotalCount ?? 0,
      pageIndex: response.pageIndex ?? response.PageIndex ?? 0,
      pageSize: response.pageSize ?? response.PageSize ?? 10,
      totalAmountSum: response.totalAmountSum ?? response.TotalAmountSum ?? 0,
    };
  }
}
